import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';

// TS port of the source (Falah) app's pages/CustomerForm.jsx. Matches
// CustomerController::validated() on the backend exactly (name required,
// phone/email/notes optional) — see api/app/Http/Controllers/CustomerController.php.
interface CustomerFormState {
  name: string;
  phone: string;
  email: string;
  notes: string;
}

const emptyForm: CustomerFormState = {
  name: '',
  phone: '',
  email: '',
  notes: '',
};

interface CustomerRecord {
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
}

type FormErrors = Record<string, string[]>;

export function CustomerForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<CustomerFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get<CustomerRecord>(`/customers/${id}`).then((res) =>
      setForm({
        name: res.data.name,
        phone: res.data.phone ?? '',
        email: res.data.email ?? '',
        notes: res.data.notes ?? '',
      })
    );
  }, [id]);

  const set =
    (key: keyof CustomerFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (id) await api.put(`/customers/${id}`, form);
      else await api.post('/customers', form);
      navigate('/revenue/customers');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>{id ? 'Edit customer' : 'Add customer'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label className="span-2">
            Name *
            <input value={form.name} onChange={set('name')} required />
            {errors.name && <small className="error">{errors.name[0]}</small>}
          </label>
          <label>
            Phone
            <input value={form.phone} onChange={set('phone')} />
            {errors.phone && <small className="error">{errors.phone[0]}</small>}
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={set('email')} />
            {errors.email && <small className="error">{errors.email[0]}</small>}
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/revenue/customers">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
